import { useEffect, useState } from "react";
import RightTimeApi from "@/API";
import { FaPlusSquare } from "react-icons/fa";
import { TbTableImport } from "react-icons/tb";
import { AiOutlinePlus } from "react-icons/ai";
import Modal from "@modalSrc/Modal";
import ContentEditModal from "@modals/ContentEditModal";
import ContentImportModal from "@modals/ContentImportModal";

type ContentLanguage = { languageId: number };
type ContentRow = {
  id: number;
  contentType?: number;
  contentTypeName?: string;
  name?: string;
  companyId?: number | string;
  contentTypeId?: number | string;
  languages: ContentLanguage[];
};
type Language = { id: number; abbrev?: string; name: string };
type ContentType = { id: number; name: string };
type ContentData = { contents: ContentRow[]; languages: Language[]; contentTypes: ContentType[] };

type CompanyContentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  companyId?: number | string;
  updateSpinner?: (isLoading: boolean) => void;
  selectMode?: boolean;
  selectedContentId?: number | string | null;
  contentTypeId?: number | string | null;
  onSelectContent?: (content: ContentRow) => void | Promise<void>;
  onDeselectContent?: () => void | Promise<void>;
};

export default function CompanyContentModal({
  isOpen,
  onClose,
  companyId,
  updateSpinner,
  selectMode = false,
  selectedContentId = null,
  contentTypeId = null,
  onSelectContent,
  onDeselectContent,
}: CompanyContentModalProps) {
  const [contentData, updateContentData] = useState<ContentData>({ contents: [], languages: [], contentTypes: [] });
  const [sortedContents, updateSortedContents] = useState<ContentRow[]>([]);
  const [isContentEditModalOpen, setIsContentEditModalOpen] = useState(false);
  const [isContentImportModalOpen, setIsContentImportModalOpen] = useState(false);
  const [modalSelectedRow, setModalSelectedRow] = useState<ContentRow | null>(null);
  const [selectedContentRowId, setSelectedContentRowId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      getContentDataAsync();
    }
  }, [isOpen, companyId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedContentRowId(null);
      return;
    }

    const parsedSelectedId = Number(selectedContentId || 0);
    setSelectedContentRowId(Number.isFinite(parsedSelectedId) && parsedSelectedId > 0 ? parsedSelectedId : null);
  }, [isOpen, selectedContentId]);

  async function getContentDataAsync() {
    if (!companyId) return;
    if (updateSpinner) updateSpinner(true);
    const resp = await RightTimeApi.getAsync(`/Company/${companyId}/Content`);
    if (updateSpinner) updateSpinner(false);
    if (resp && resp.ok && resp.jsonData) {
      const data = (resp.jsonData || { contents: [], languages: [], contentTypes: [] }) as ContentData;
      updateContentData(data);
      const sortedData = [...data.contents].sort(
        (a, b) => a.contentType - b.contentType
      );
      updateSortedContents(sortedData);
    }
  }

  async function onAddContentAsync() {
    setModalSelectedRow({
      id: 0,
      contentTypeId: Number(contentTypeId || 0) > 0 ? Number(contentTypeId) : "",
      name: "",
      companyId: companyId,
      languages: [],
    });
    setIsContentEditModalOpen(true);
  }

  async function handleContentEditModalCloseAsync() {
    setIsContentEditModalOpen(false);
    setModalSelectedRow(null);
    await getContentDataAsync();
  }

  async function handleContentImportModalCloseAsync() {
    setIsContentImportModalOpen(false);
  }

  async function handleContentImportedAsync() {
    await getContentDataAsync();
  }

  async function onSelectContentAsync() {
    if (!selectMode || !onSelectContent || !selectedContentRowId) return;

    const selectedContent = filteredContents.find((content) => Number(content.id) === Number(selectedContentRowId));
    if (!selectedContent) return;

    await onSelectContent(selectedContent);
  }

  async function onDeselectContentAsync() {
    if (!selectMode || !onDeselectContent) return;
    await onDeselectContent();
  }

  const filteredContents = Number(contentTypeId || 0) > 0
    ? sortedContents.filter((content) => Number(content.contentTypeId || content.contentType || 0) === Number(contentTypeId))
    : sortedContents;

  const footer = selectMode
    ? (
      <div className="footerRight">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={onDeselectContentAsync}
        >
          Deselect
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSelectContentAsync}
          disabled={!selectedContentRowId}
        >
          Select
        </button>
      </div>
    )
    : undefined;

  if (!isOpen) return null;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={selectMode ? "Select Content" : "Company Content"}
        size="lg"
        className={selectMode ? "companyContentModal selectModal" : "companyContentModal"}
        footer={footer}
      >
        <div className="tableWrapper dimensionTable">
          <table>
            <caption>
              <span className="title">Content</span>
              {!selectMode && (
                <>
                  <span
                    className="actionIcon plusIcon floatRight"
                    onClick={() => setIsContentImportModalOpen(true)}
                    title="Import Content"
                    data-help-id="companyContentImport"
                    style={{ cursor: "pointer", display: "inline-block", marginLeft: 8 }}
                  >
                    <TbTableImport />
                  </span>
                  <span
                    className="actionIcon plusIcon floatRight"
                    onClick={onAddContentAsync}
                    title="Add Content"
                    style={{ cursor: "pointer", display: "inline-block", marginLeft: 8 }}
                  >
                    <FaPlusSquare />
                  </span>
                </>
              )}
            </caption>
            <thead>
              <tr>
                <th>Id</th>
                <th>Type</th>
                <th>Name</th>
                <th>Languages</th>
              </tr>
            </thead>
            <tbody>
              {filteredContents && filteredContents.length > 0
                ? filteredContents.map((content) => {
                    const langAbbrList =
                      content.languages && content.languages.length > 0
                        ? content.languages
                            .map(
                              (l) =>
                                contentData.languages.find(
                                  (lang) => lang.id === l.languageId
                                )?.abbrev || ""
                            )
                            .filter(Boolean)
                            .join(", ")
                        : "";
                    const isSelected = Number(selectedContentRowId || 0) > 0 && Number(content.id) === Number(selectedContentRowId);
                    return (
                      <tr
                        key={content.id}
                        onClick={() => {
                          if (selectMode) {
                            setSelectedContentRowId(Number(content.id));
                            return;
                          }

                          setModalSelectedRow(content);
                          setIsContentEditModalOpen(true);
                        }}
                        className={isSelected ? "active" : ""}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{content.id}</td>
                        <td>{content.contentTypeName}</td>
                        <td>{content.name}</td>
                        <td
                          className="center clickable-cell"
                          title={
                            langAbbrList
                              ? `Edit content for: ${langAbbrList}`
                              : "Add content"
                          }
                        >
                          {langAbbrList ? (
                            langAbbrList
                          ) : (
                            <AiOutlinePlus className="actionIcon plusIcon" />
                          )}
                        </td>
                      </tr>
                    );
                  })
                : null}
            </tbody>
          </table>
        </div>
      </Modal>
      <ContentEditModal
        isOpen={isContentEditModalOpen}
        onClose={handleContentEditModalCloseAsync}
        companyId={Number(companyId) || 0}
        companyContentId={modalSelectedRow?.id ?? 0}
        contentTypeId={Number(contentTypeId || 0) > 0 ? Number(contentTypeId) : undefined}
      />
      <ContentImportModal
        isOpen={isContentImportModalOpen}
        onClose={handleContentImportModalCloseAsync}
        companyId={companyId}
        updateSpinner={updateSpinner}
        onImported={handleContentImportedAsync}
      />
    </>
  );
}


